import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  ClipboardText,
  Gauge,
  MagnifyingGlass,
  PencilSimple,
  ShieldCheck,
  UploadSimple,
  Users,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getActionableError } from "@/lib/errors";
import { useAuth } from "@/context/AuthContext";
import { trackProductEvent } from "@/lib/productAnalytics";

const guideSteps = [
  {
    eyebrow: "Your first useful result",
    title: "Start with one real customer call.",
    copy: "Choose a conversation you already understand. That makes it easy to judge whether Callsect recovered the right relationship context, signals, and next move.",
    icon: UploadSimple,
  },
  {
    eyebrow: "What to review first",
    title: "Begin with the Dissection, not the transcript.",
    copy: "Callsect separates evidence, interpretation, and action so you can see what changed without reading the entire conversation again.",
    icon: MagnifyingGlass,
  },
  {
    eyebrow: "Where the value compounds",
    title: "Reuse the same customer name next time.",
    copy: "Each new call extends the same relationship memory. Callsect remembers how objections, intent, commitments, and stakeholders evolve.",
    icon: Users,
  },
];

const resultCards = [
  {
    icon: MagnifyingGlass,
    title: "Dissection",
    copy: "What happened, what changed, and what deserves attention.",
  },
  {
    icon: ClipboardText,
    title: "CRM Notes",
    copy: "Document the call in your voice with CRM-ready formats shaped by your Writing DNA.",
  },
  {
    icon: CheckCircle,
    title: "Coaching",
    copy: "The strongest evidence-backed move to improve what happens next.",
  },
];

function valueOrUnlimited(value) {
  return value === null || value === undefined ? "Unlimited" : value;
}

function allowance(used, limit, remaining) {
  if (limit === null || limit === undefined) return "Owner exemption — no beta quota";
  return `${used ?? 0} of ${limit} used · ${remaining ?? 0} remaining`;
}

export default function BetaQuickStartDialog({ open, onClose, persistOnClose = false }) {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const panelRef = useRef(null);
  const trackedOpenRef = useRef(false);
  const [activeStep, setActiveStep] = useState(0);
  const [limits, setLimits] = useState(null);
  const [loadingLimits, setLoadingLimits] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!open) {
      trackedOpenRef.current = false;
      return undefined;
    }
    let active = true;
    setActiveStep(0);
    if (!trackedOpenRef.current) {
      trackProductEvent("guide_opened", { section: persistOnClose ? "first_login" : "account" });
      trackedOpenRef.current = true;
    }
    setLoadingLimits(true);
    api.get("/beta/limits")
      .then(({ data }) => active && setLimits(data))
      .catch(() => active && setLimits(null))
      .finally(() => active && setLoadingLimits(false));

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => panelRef.current?.focus());

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !persistOnClose) onClose();
      if (event.key === "ArrowRight") {
        setActiveStep((current) => Math.min(current + 1, guideSteps.length - 1));
      }
      if (event.key === "ArrowLeft") {
        setActiveStep((current) => Math.max(current - 1, 0));
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      active = false;
      document.body.style.overflow = previousOverflow;
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open, persistOnClose]);

  if (!open) return null;

  const finish = async (path = null) => {
    if (closing) return;
    setClosing(true);
    try {
      if (persistOnClose) {
        await api.post("/account/onboarding-seen");
        await refreshUser();
      }
      trackProductEvent(path === "/upload" ? "guide_completed" : "guide_skipped", {
        section: persistOnClose ? "first_login" : "account",
        outcome: path || "closed",
      });
      onClose();
      if (path) navigate(path);
    } catch (error) {
      toast.error(getActionableError(error, "Could not save the quick-start guide status."));
    } finally {
      setClosing(false);
    }
  };

  const step = guideSteps[activeStep];
  const StepIcon = step.icon;
  const isLastStep = activeStep === guideSteps.length - 1;

  return (
    <div className="beta-guide beta-guide--polished" role="dialog" aria-modal="true" aria-labelledby="beta-guide-title">
      <div className="beta-guide__backdrop" />
      <section className="beta-guide__panel" ref={panelRef} tabIndex={-1}>
        <header className="beta-guide__header beta-guide__header--compact">
          <div className="beta-guide__progress-copy">
            <div className="brand-eyebrow">Private beta quick start</div>
            <span>Step {activeStep + 1} of {guideSteps.length}</span>
          </div>
          {!persistOnClose && (
            <button type="button" className="beta-guide__close" onClick={onClose} aria-label="Close guide">
              <X size={19} />
            </button>
          )}
        </header>

        <div className="beta-guide__progress" aria-label={`Step ${activeStep + 1} of ${guideSteps.length}`}>
          {guideSteps.map((item, index) => (
            <button
              type="button"
              key={item.title}
              className={`${index === activeStep ? "is-active" : ""}${index < activeStep ? " is-complete" : ""}`}
              onClick={() => setActiveStep(index)}
              aria-label={`Open step ${index + 1}: ${item.title}`}
            />
          ))}
        </div>

        <div className="beta-guide__body beta-guide__body--focused">
          <section className="beta-guide__story">
            <span className="beta-guide__story-icon" aria-hidden="true">
              <StepIcon size={30} weight="duotone" />
            </span>
            <div className="brand-eyebrow">{step.eyebrow}</div>
            <h2 id="beta-guide-title">{step.title}</h2>
            <p>{step.copy}</p>

            {activeStep === 0 && (
              <div className="beta-guide__first-call-checklist">
                <GuideCheck icon={UploadSimple} title="Use a clear recording" copy="MP3, WAV, AAC, or M4A works best." />
                <GuideCheck icon={ShieldCheck} title="Confirm recording authority" copy="Only upload conversations you are authorised to process." />
                <GuideCheck icon={PencilSimple} title="Add simple context" copy="A call title, customer, and date are enough." />
              </div>
            )}

            {activeStep === 1 && (
              <div className="beta-guide__result-grid">
                {resultCards.map(({ icon: Icon, title, copy }) => (
                  <article key={title}>
                    <Icon size={19} weight="duotone" />
                    <strong>{title}</strong>
                    <span>{copy}</span>
                  </article>
                ))}
              </div>
            )}

            {activeStep === 2 && (
              <div className="beta-guide__continuity-note">
                <Users size={21} weight="duotone" />
                <div>
                  <strong>Consistency matters more than perfect naming.</strong>
                  <span>Start typing the same customer or company and select the saved suggestion. Callsect will connect the history automatically.</span>
                </div>
              </div>
            )}
          </section>

          <aside className="beta-guide__limits beta-guide__limits--polished">
            <div className="beta-guide__limits-heading">
              <span aria-hidden="true"><Gauge size={21} weight="duotone" /></span>
              <div>
                <div className="brand-eyebrow">Your current allowances</div>
                <h3>Private-beta limits</h3>
              </div>
            </div>

            {loadingLimits ? (
              <div className="beta-guide__limits-skeleton" aria-label="Loading current beta limits">
                <span /><span /><span />
              </div>
            ) : limits ? (
              <div className="beta-guide__limit-grid beta-guide__limit-grid--compact">
                <LimitCard label="Saved call uploads" value={allowance(limits.uploaded_calls_used, limits.uploaded_calls_limit, limits.uploaded_calls_remaining)} />
                <LimitCard label="Maximum recording" value={`${valueOrUnlimited(limits.max_audio_minutes)} minutes`} />
                <LimitCard label="Maximum file size" value={`${valueOrUnlimited(limits.max_upload_mb)} MB`} />
                <LimitCard label="AI generations" value={limits.generations_per_call == null ? "Owner exemption" : `Up to ${limits.generations_per_call} generations per call output`} />
              </div>
            ) : (
              <p className="beta-guide__limits-loading">Your live allowance counters could not be loaded. They remain visible on New Call.</p>
            )}

            <p className="beta-guide__limits-note">
              These are Callsect private-beta allowance counters, not OpenAI charges or a bill to you.
            </p>
          </aside>
        </div>

        <footer className="beta-guide__actions beta-guide__actions--polished">
          <button
            type="button"
            className="beta-guide__secondary"
            onClick={() => {
              if (activeStep > 0) {
                setActiveStep((current) => current - 1);
              } else if (persistOnClose) {
                finish("/dashboard");
              } else {
                trackProductEvent("guide_skipped", { section: "account", outcome: "closed" });
                onClose();
              }
            }}
            disabled={closing}
          >
            {activeStep > 0 && <ArrowLeft size={16} />}
            {activeStep > 0 ? "Back" : persistOnClose ? "Explore first" : "Close"}
          </button>

          {!isLastStep ? (
            <button type="button" className="beta-guide__primary" onClick={() => setActiveStep((current) => current + 1)}>
              Continue
              <ArrowRight size={17} weight="bold" />
            </button>
          ) : (
            <button type="button" className="beta-guide__primary" onClick={() => finish("/upload")} disabled={closing}>
              <CheckCircle size={18} weight="fill" />
              {closing ? "Saving…" : persistOnClose ? "Start with my first call" : "Open New Call"}
            </button>
          )}
        </footer>
      </section>
    </div>
  );
}

function GuideCheck({ icon: Icon, title, copy }) {
  return (
    <article>
      <span aria-hidden="true"><Icon size={18} weight="duotone" /></span>
      <div>
        <strong>{title}</strong>
        <small>{copy}</small>
      </div>
    </article>
  );
}

function LimitCard({ label, value }) {
  return (
    <article className="beta-guide__limit-card">
      <small>{label}</small>
      <strong>{value}</strong>
    </article>
  );
}
